import {
  Stack,
  Title,
  Paper,
  Text,
  TextInput,
  Button,
  Table,
  Checkbox,
  Modal,
  Select,
  NumberInput,
  Textarea,
  Group,
  Box,
  ActionIcon,
  Menu,
  Divider,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { useState, useEffect } from "react";
import { notifications } from "@mantine/notifications";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";
import {
  getDiaryWorkPhases,
  getPerceptionInvoice,
  getObjectDiaryEntries,
  saveDiaryWorkPhasesAndPerception,
  getDiaryTemplateName,
  getSupervisorsByPerception,
  getObjectDiaryById,
  deleteObjectDiaryById,
} from "../../../services/objectmodelservice";
import AddEditDiaryEntryModal, {
  DiaryEntryForm,
} from "./modals/AddEditDiaryEntryModal";
import { DiaryWorkPhaseDto } from "../../../types/DiaryWorkPhaseDto";
import { ObjectDiaryEntryDto } from "../../../types/ObjectDiaryEntryDto";
import { useTranslation } from "../../../hooks/useTranslation";
import useTransientStore from "../../../data/transcientStore";

interface DiaryEntry {
  id: number;
  model: number | null;
  date: Date | null;
  amended: string;
  weather: string;
  valvoja: string;
  tyonjohtaja: string;
  tyonjohtaja2: string;
  workingGroup: string;
  equipment: string;
  diesel: number;
  mpo: number;
  jobRemarks: string;
  clientRemarks: string;
  notesToClient: string;
  others: string;
}

interface WorkStepRow {
  tyovaiheen: string;
  aloitus: Date | null;
  lopetus: Date | null;
  eiPaivamaaria: boolean;
  isFromApi?: boolean;
  mandate?: string; // "Yes" | "No"
  workPhaseId?: number; // lookup id used for saving
}

export interface DiaryTabProps {
  form: any; // Will be typed more specifically
  addressSystemId?: number;
  userId?: number;
  canModify?: boolean;
}

export default function DiaryTab({
  form,
  addressSystemId,
  userId,
  canModify = true,
}: DiaryTabProps) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 327,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });
  const [diaryModalOpened, setDiaryModalOpened] = useState(false);
  const [diaryEntries, setDiaryEntries] = useState<DiaryEntry[]>([]);
  const [editingDiaryId, setEditingDiaryId] = useState<number | null>(null);
  const [workSteps, setWorkSteps] = useState<WorkStepRow[]>([
    { tyovaiheen: "", aloitus: null, lopetus: null, eiPaivamaaria: false },
  ]);
  const [urakoitsijaNumber, setUrakoitsijaNumber] = useState("");
  const [mittausPvm, setMittausPvm] = useState<Date | null>(null);
  const [mittausPerformers, setMittausPerformers] = useState("");
  const [positionMeasurementPvm, setPositionMeasurementPvm] =
    useState<Date | null>(null);
  const [positionPerformers, setPositionPerformers] = useState("");
  const [loadedCount, setLoadedCount] = useState<number>(0);
  const [loadError, setLoadError] = useState<string>("");
  const [templateOptions, setTemplateOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [supervisorOptions, setSupervisorOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [foremanOptions, setForemanOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [foreman2Options, setForeman2Options] = useState<
    { value: string; label: string }[]
  >([]);
  const [modalResetToken, setModalResetToken] = useState<number>(0);

  const initialModalForm: DiaryEntryForm = {
    model: null,
    date: new Date(),
    weather: "",
    valvoja: "",
    tyonjohtaja: "",
    tyonjohtaja2: "",
    workingGroup: "",
    equipment: "",
    diesel: 0,
    mpo: 0,
    jobRemarks: "",
    clientRemarks: "",
    notesToClient: "",
    others: "",
  };

  // Modal form state
  const [modalForm, setModalForm] = useState<DiaryEntryForm>({
    model: null,
    date: new Date(),
    // 'amended' is derived/display-only, not part of modalForm
    weather: "",
    valvoja: "",
    tyonjohtaja: "",
    tyonjohtaja2: "",
    workingGroup: "",
    equipment: "",
    diesel: 0,
    mpo: 0,
    jobRemarks: "",
    clientRemarks: "",
    notesToClient: "",
    others: "",
  });

  const addWorkStep = () => {
    setWorkSteps([
      ...workSteps,
      { tyovaiheen: "", aloitus: null, lopetus: null, eiPaivamaaria: false },
    ]);
  };

  const updateWorkStep = (
    index: number,
    field: keyof WorkStepRow,
    value: any
  ) => {
    const updated = [...workSteps];
    updated[index] = { ...updated[index], [field]: value };
    setWorkSteps(updated);
  };

  const removeWorkStep = (index: number) => {
    if (workSteps.length > 1) {
      setWorkSteps(workSteps.filter((_, i) => i !== index));
    }
  };

  // Derive identifiers per render; keep effect deps simple
  const addrId =
    addressSystemId !== undefined
      ? addressSystemId
      : (form.values as any)?.addressSystemId ||
        (form.values as any)?.addressSystem?.id;

  // Derive IDs from the shared form; fall back to fields used in InformationTab
  // contractId often comes from selected job (string id) in InformationTab
  const contractId: number | undefined = (() => {
    const explicit = form?.values?.contractId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const job = form?.values?.job; // string id from Select
    if (typeof job === "string" && job.length > 0) {
      const parsed = parseInt(job, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  })();

  // objectId may be stored as `objectId` or the main object `id` in InformationTab
  const objectId: number | undefined = (() => {
    const explicit = form?.values?.objectId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const objId = form?.values?.id;
    if (typeof objId === "number" && objId > 0) return objId;
    return undefined;
  })();

  const handleAddDiaryEntry = (result?: { id: number; created: boolean }) => {
    // Always refresh from backend after successful upsert so the table reflects source of truth
    if (contractId && objectId) {
      getObjectDiaryEntries(contractId!, objectId!)
        .then((rows: ObjectDiaryEntryDto[]) => {
          const mapped: DiaryEntry[] = rows.map((r) => ({
            id: r.id,
            date: r.diaryDate ? new Date(r.diaryDate) : null,
            model: null,
            amended: r.modifier || "",
            weather: "",
            valvoja: "",
            tyonjohtaja: "",
            tyonjohtaja2: "",
            workingGroup: "",
            equipment: "",
            diesel: 0,
            mpo: 0,
            jobRemarks: "",
            clientRemarks: "",
            notesToClient: "",
            others: "",
          }));
          setDiaryEntries(mapped);
        })
        .catch(() => {
          // Non-blocking; keep modal close regardless
        })
        .finally(() => {
          setEditingDiaryId(null);
          setDiaryModalOpened(false);
          setModalForm({
            model: null,
            date: new Date(),
            weather: "",
            valvoja: "",
            tyonjohtaja: "",
            tyonjohtaja2: "",
            workingGroup: "",
            equipment: "",
            diesel: 0,
            mpo: 0,
            jobRemarks: "",
            clientRemarks: "",
            notesToClient: "",
            others: "",
          });
        });
    } else {
      // Fallback: just close modal and reset
      setEditingDiaryId(null);
      setDiaryModalOpened(false);
      setModalForm({
        model: null,
        date: new Date(),
        weather: "",
        valvoja: "",
        tyonjohtaja: "",
        tyonjohtaja2: "",
        workingGroup: "",
        equipment: "",
        diesel: 0,
        mpo: 0,
        jobRemarks: "",
        clientRemarks: "",
        notesToClient: "",
        others: "",
      });
    }
  };

  const handleEditDiaryEntry = (entry: DiaryEntry) => {
    // Load full diary details, prefill modal, then open; track editing id
    setEditingDiaryId(entry.id);
    if (!contractId || !objectId) {
      // fallback to existing prefill
      setModalForm((prev) => ({
        ...prev,
        date: entry.date ?? prev.date,
        model: entry.id,
      }));
      openPrefilledDiaryModal(true);
      return;
    }
    getObjectDiaryById(entry.id, contractId, objectId)
      .then((dto) => {
        setModalForm((prev) => ({
          ...prev,
          date: dto.diaryDate
            ? new Date(dto.diaryDate)
            : entry.date ?? prev.date,
          weather: dto.weather ?? prev.weather,
          valvoja:
            typeof dto.supervisorId === "number" && dto.supervisorId > 0
              ? String(dto.supervisorId)
              : prev.valvoja,
          tyonjohtaja:
            typeof dto.foremanId === "number" && dto.foremanId > 0
              ? String(dto.foremanId)
              : prev.tyonjohtaja,
          tyonjohtaja2:
            typeof dto.viseForemanId === "number" && dto.viseForemanId > 0
              ? String(dto.viseForemanId)
              : prev.tyonjohtaja2,
          workingGroup: dto.workers ?? prev.workingGroup,
          equipment: dto.machines ?? prev.equipment,
          diesel: dto.dieselConsumption ?? prev.diesel,
          mpo: dto.mpoConsumption ?? prev.mpo,
          jobRemarks: dto.tasks ?? prev.jobRemarks,
          clientRemarks: dto.subscriberNotices ?? prev.clientRemarks,
          notesToClient: dto.noticesToSubscriber ?? prev.notesToClient,
          others: dto.description ?? prev.others,
          model: entry.id,
        }));
      })
      .catch(() => {
        // fallback to minimal prefill
        setModalForm((prev) => ({
          ...prev,
          date: entry.date ?? prev.date,
          model: entry.id,
        }));
      })
      .finally(() => {
        openPrefilledDiaryModal(true);
      });
  };

  const handleDeleteDiaryEntry = (id: number) => {
    if (!id) return;
    const ok = window.confirm(
      getTranslation("PROMPT_DELETE_DIARY_ENTRY") ||
        "Delete this diary entry?"
    );
    if (!ok) return;
    if (contractId && objectId) {
      // Attempt server-side delete then refresh
      deleteObjectDiaryById(id, contractId!, objectId!)
        .then((deleted) => {
          if (deleted) {
            getObjectDiaryEntries(contractId!, objectId!)
              .then((rows: ObjectDiaryEntryDto[]) => {
                const mapped: DiaryEntry[] = rows.map((r) => ({
                  id: r.id,
                  date: r.diaryDate ? new Date(r.diaryDate) : null,
                  model: null,
                  amended: r.modifier || "",
                  weather: "",
                  valvoja: "",
                  tyonjohtaja: "",
                  tyonjohtaja2: "",
                  workingGroup: "",
                  equipment: "",
                  diesel: 0,
                  mpo: 0,
                  jobRemarks: "",
                  clientRemarks: "",
                  notesToClient: "",
                  others: "",
                }));
                setDiaryEntries(mapped);
              })
              .catch(() => {
                // fallback to local removal if refresh fails
                setDiaryEntries((prev) => prev.filter((e) => e.id !== id));
              });
            notifications.show({
              title: getTranslation("TITLE_DELETED") || "Deleted",
              message:
                getTranslation("MESSAGE_DIARY_ENTRY_REMOVED") ||
                "Diary entry removed.",
              color: "green",
              autoClose: 3000,
              position: "top-center",
            });
          } else {
            throw new Error("Delete failed");
          }
        })
        .catch(() => {
          // fallback local removal
          setDiaryEntries((prev) => prev.filter((e) => e.id !== id));
          notifications.show({
            title: getTranslation("TITLE_NOTICE") || "Notice",
            message:
              getTranslation("MESSAGE_LOCAL_REMOVAL_SERVER_DELETE_FAILED") ||
              "Local removal; server delete failed.",
            color: "yellow",
            autoClose: 3000,
            position: "top-center",
          });
        });
    } else {
      // no identifiers; perform local removal only
      setDiaryEntries((prev) => prev.filter((e) => e.id !== id));
      notifications.show({
        title: getTranslation("TITLE_DELETED") || "Deleted",
        message:
          getTranslation("MESSAGE_DIARY_ENTRY_REMOVED") ||
          "Diary entry removed.",
        color: "green",
        autoClose: 3000,
        position: "top-center",
      });
    }
  };

  // Prefill modal with template/info before opening
  const openPrefilledDiaryModal = async (isEdit: boolean = false) => {
    try {
      if (!isEdit) {
        // New entry: clear all fields including template
        setEditingDiaryId(null);
        setModalForm({ ...initialModalForm });
        setModalResetToken((x) => x + 1);
      }
      if (!addrId || !objectId) {
        setDiaryModalOpened(true);
        return;
      }
      if (isEdit) {
        // For edit, we don't override with template/info to avoid wiping edited values
      } else {
        // For new, we intentionally keep fields cleared (no prefill)
      }

      // Fetch template name list for model select (independent of prefill)
      try {
        if (!contractId) {
          setTemplateOptions([]);
        } else {
          const names = await getDiaryTemplateName(
            addrId,
            objectId!,
            contractId,
            userId || 0
          );
          let opts: { value: number; label: string }[] = Array.isArray(names)
            ? names.map((n) => ({ value: n.id, label: n.rowName }))
            : [];
          // Ensure currently edited diary id is present so it can be auto-selected
          if (editingDiaryId && !opts.some((o) => o.value === editingDiaryId)) {
            const labelDate = (
              modalForm.date ? modalForm.date : new Date()
            ).toLocaleDateString();
            opts = [
              { value: editingDiaryId, label: `${labelDate} / Current` },
              ...opts,
            ];
          }
          setTemplateOptions(opts);
        }
      } catch {
        setTemplateOptions([]);
      }

      // Fetch supervisors (contractor=false)
      try {
        const sups = await getSupervisorsByPerception(addrId, objectId!, false);
        const opts = Array.isArray(sups)
          ? sups.map((s) => ({ value: String(s.id), label: s.name }))
          : [];
        setSupervisorOptions(opts);
      } catch {
        setSupervisorOptions([]);
      }

      // Fetch contractor foremen (contractor=true) for both foreman selects
      try {
        const consups = await getSupervisorsByPerception(
          addrId,
          objectId!,
          true
        );
        const fopts = Array.isArray(consups)
          ? consups.map((s) => ({ value: String(s.id), label: s.name }))
          : [];
        setForemanOptions(fopts);
        setForeman2Options(fopts);
      } catch {
        setForemanOptions([]);
        setForeman2Options([]);
      }
    } catch {
      // silently open without prefill on error
    } finally {
      setDiaryModalOpened(true);
    }
  };

  const cancelDiaryChanges = () => {
    // Reset local editable fields to defaults
    setWorkSteps([
      { tyovaiheen: "", aloitus: null, lopetus: null, eiPaivamaaria: false },
    ]);
    setUrakoitsijaNumber("");
    setMittausPvm(null);
    setMittausPerformers("");
    setPositionMeasurementPvm(null);
    setPositionPerformers("");
    setDiaryEntries([]);
    setLoadError("");
  };

  const saveDiaryData = async () => {
    if (!addrId || !contractId || !objectId) {
      setLoadError(
        getTranslation("MESSAGE_MISSING_IDENTIFIERS_TO_SAVE") ||
          "Missing identifiers to save"
      );
      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message:
          getTranslation("MESSAGE_MISSING_IDENTIFIERS_TO_SAVE") ||
          "Missing identifiers to save",
        color: "red",
        autoClose: 5000,
        position: "top-center",
        classNames: { root: "notification-error" },
      });
      return;
    }

    const payload = {
      addressSystemId: addrId as number,
      contractId: contractId as number,
      objectId: objectId as number,
      contractorWorkNumber: urakoitsijaNumber || null,
      measurementPersons: mittausPerformers || null,
      measurementDate: mittausPvm ? mittausPvm.toISOString() : null,
      locationMeasurementPersons: positionPerformers || null,
      locationMeasurementDate: positionMeasurementPvm
        ? positionMeasurementPvm.toISOString()
        : null,
      workPhases: workSteps
        .filter((w) => typeof w.workPhaseId === "number")
        .map((w) => ({
          workPhaseId: w.workPhaseId as number,
          startDate: w.aloitus ? w.aloitus.toISOString() : null,
          endDate: w.lopetus ? w.lopetus.toISOString() : null,
          noDates: !!w.eiPaivamaaria,
        })),
    };

    try {
      const ok = await saveDiaryWorkPhasesAndPerception(payload as any);
      if (ok) {
        notifications.show({
          title: getTranslation("TITLE_SUCCESS") || "Success",
          message:
            getTranslation("MESSAGE_DIARY_DATA_SAVED") ||
            "Diary data saved successfully.",
          color: "green",
          autoClose: 5000,
          position: "top-center",
          classNames: { root: "notification-success" },
        });
        // Re-fetch latest values to ensure UI reflects saved state
        try {
          const [phases, invoice] = await Promise.all([
            getDiaryWorkPhases(addrId, contractId!, objectId!),
            getPerceptionInvoice(addrId, objectId!),
          ]);
          // Update work steps
          if (Array.isArray(phases) && phases.length > 0) {
            const rows: WorkStepRow[] = phases.map((d: DiaryWorkPhaseDto) => ({
              tyovaiheen: d.description || "",
              aloitus: d.startDate ? new Date(d.startDate) : null,
              lopetus: d.endDate ? new Date(d.endDate) : null,
              eiPaivamaaria: !!d.noDates,
              isFromApi: true,
              mandate: (d as any).mandate || "No",
              workPhaseId: (d as any).id,
            }));
            setWorkSteps(rows);
            setLoadedCount(rows.length);
          } else {
            setWorkSteps([
              {
                tyovaiheen: "",
                aloitus: null,
                lopetus: null,
                eiPaivamaaria: false,
              },
            ]);
            setLoadedCount(0);
          }
          // Update perception-related fields
          setUrakoitsijaNumber(invoice.contractorWorknumber ?? "");
          setMittausPvm(
            invoice.measurementDate ? new Date(invoice.measurementDate) : null
          );
          setMittausPerformers(invoice.measurementPersons ?? "");
          setPositionMeasurementPvm(
            invoice.locationMeasurementDate
              ? new Date(invoice.locationMeasurementDate)
              : null
          );
          setPositionPerformers(invoice.locationMeasurementPersons ?? "");
        } catch (refreshErr) {
          // Non-blocking: surface as a warning but keep success
          notifications.show({
            title: getTranslation("TITLE_NOTICE") || "Notice",
            message:
              getTranslation("MESSAGE_SAVED_BUT_REFRESH_FAILED") ||
              "Saved, but failed to refresh data.",
            color: "yellow",
            autoClose: 4000,
            position: "top-center",
          });
        }
      } else {
        setLoadError(
          getTranslation("MESSAGE_DIARY_DATA_SAVE_FAILED") ||
            "Failed to save diary data"
        );
        notifications.show({
          title: getTranslation("TITLE_ERROR") || "Error",
          message:
            getTranslation("MESSAGE_DIARY_DATA_SAVE_FAILED") ||
            "Failed to save diary data.",
          color: "red",
          autoClose: 5000,
          position: "top-center",
          classNames: { root: "notification-error" },
        });
      }
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : getTranslation("MESSAGE_DIARY_DATA_SAVE_FAILED") ||
            "Failed to save diary data";
      setLoadError(msg);
      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message: msg,
        color: "red",
        autoClose: 5000,
        position: "top-center",
        classNames: { root: "notification-error" },
      });
    }
  };

  // Load diary work phases from backend and populate rows
  useEffect(() => {
    if (!addrId || !contractId || !objectId) {
      console.debug("DiaryTab: missing identifiers", {
        addrId,
        contractId,
        objectId,
      });
      return;
    }
    setLoadError("");
    getDiaryWorkPhases(addrId, contractId, objectId)
      .then((data: DiaryWorkPhaseDto[]) => {
        if (!Array.isArray(data) || data.length === 0) {
          setWorkSteps([
            {
              tyovaiheen: "",
              aloitus: null,
              lopetus: null,
              eiPaivamaaria: false,
            },
          ]);
          setLoadedCount(0);
          return;
        }
        const rows: WorkStepRow[] = data.map((d) => ({
          tyovaiheen: d.description || "",
          aloitus: d.startDate ? new Date(d.startDate) : null,
          lopetus: d.endDate ? new Date(d.endDate) : null,
          eiPaivamaaria: !!d.noDates,
          isFromApi: true,
          mandate: (d as any).mandate || "No",
          workPhaseId: d.id,
        }));
        setWorkSteps(rows);
        setLoadedCount(rows.length);
      })
      .catch(() => {
        setLoadError(
          getTranslation("MESSAGE_WORK_PHASES_LOAD_FAILED") ||
            "Failed to load work phases"
        );
      });

    // Load perception invoice-like fields for binding
    getPerceptionInvoice(addrId, objectId)
      .then((dto) => {
        setUrakoitsijaNumber(dto.contractorWorknumber ?? "");
        setMittausPvm(
          dto.measurementDate ? new Date(dto.measurementDate) : null
        );
        setMittausPerformers(dto.measurementPersons ?? "");
        setPositionMeasurementPvm(
          dto.locationMeasurementDate
            ? new Date(dto.locationMeasurementDate)
            : null
        );
        setPositionPerformers(dto.locationMeasurementPersons ?? "");
      })
      .catch(() => {
        // non-blocking; keep UI usable
      });

    // Load diary entries list
    getObjectDiaryEntries(contractId!, objectId!)
      .then((rows: ObjectDiaryEntryDto[]) => {
        const mapped: DiaryEntry[] = rows.map((r) => ({
          id: r.id,
          date: r.diaryDate ? new Date(r.diaryDate) : null,
          model: null,
          amended: r.modifier || "",
          weather: "",
          valvoja: "",
          tyonjohtaja: "",
          tyonjohtaja2: "",
          workingGroup: "",
          equipment: "",
          diesel: 0,
          mpo: 0,
          jobRemarks: "",
          clientRemarks: "",
          notesToClient: "",
          others: "",
        }));
        setDiaryEntries(mapped);
      })
      .catch(() => {
        // silent failure; table will show 'No diary entries yet'
      });
  }, [addressSystemId, form.values]);

  return (
    <>
      <Stack>
        {/* <Title order={4}>Diary</Title> */}
        <Title order={4}>{`${
          getTranslation("TITLE_DIARY") || "Diary"
        } - ${
          // Prefer explicit jobName set by InformationTab, then contractName; remove object name from title
          form?.values?.jobName && String(form.values.jobName).trim()
            ? String(form.values.jobName).trim()
            : form?.values?.contractName &&
              String(form.values.contractName).trim()
            ? String(form.values.contractName).trim()
            : getTranslation("LABEL_CONTRACT") || "Contract"
        }`}</Title>
        {/* Job Information Table */}
        <Paper p="md" withBorder>
          {/* <Text fw={500} mb="md">
            Paivakurja-Job-Name
          </Text> */}
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  {getTranslation("LABEL_OPERATION_STAGE_NAME") ||
                    "Operation Stage Name"}
                </Table.Th>
                <Table.Th>
                  {getTranslation("LABEL_BEGINNING") || "Beginning"}
                </Table.Th>
                <Table.Th>{getTranslation("LABEL_ENDING") || "Ending"}</Table.Th>
                <Table.Th>
                  {getTranslation("LABEL_NO_DATES") || "No dates"}
                </Table.Th>
                {/* <Table.Th>Actions</Table.Th> */}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {workSteps.map((step, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <Text size="sm">
                      {step.tyovaiheen ? (
                        <>
                          {step.tyovaiheen}
                          {step.mandate === "Yes" && (
                            <Text span c="red">
                              {" "}
                              *
                            </Text>
                          )}
                        </>
                      ) : step.mandate === "Yes" ? (
                        <Text span c="red">
                          *
                        </Text>
                      ) : (
                        "-"
                      )}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <DatePickerInput
                      value={step.aloitus}
                      onChange={(date) =>
                        updateWorkStep(index, "aloitus", date)
                      }
                      placeholder={getTranslation("PLACEHOLDER_START_DATE") || "Start date"}
                      disabled={step.eiPaivamaaria}
                      required={step.mandate === "Yes"}
                      valueFormat="DD.MM.YYYY"
                    />
                  </Table.Td>
                  <Table.Td>
                    <DatePickerInput
                      value={step.lopetus}
                      onChange={(date) =>
                        updateWorkStep(index, "lopetus", date)
                      }
                      placeholder={getTranslation("PLACEHOLDER_END_DATE") || "End date"}
                      disabled={step.eiPaivamaaria}
                      required={step.mandate === "Yes"}
                      valueFormat="DD.MM.YYYY"
                    />
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      checked={step.eiPaivamaaria}
                      onChange={(e) =>
                        updateWorkStep(index, "eiPaivamaaria", e.target.checked)
                      }
                      disabled={step.mandate === "Yes"}
                    />
                  </Table.Td>
                  {/* <Table.Td>
                    <Button
                      variant="outline"
                      size="xs"
                      color="red"
                      onClick={() => removeWorkStep(index)}
                      disabled={workSteps.length === 1}
                    >
                      Remove
                    </Button>
                  </Table.Td> */}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {/* <Button
            variant="outline"
            size="sm"
            mt="md"
            leftSection={<IconPlus size={16} />}
            onClick={addWorkStep}
          >
            Add Work Step
          </Button> */}
        </Paper>

        {/* Other Form Fields */}
        <Group grow>
          <TextInput
            label={getTranslation("LABEL_CONTRACTORS_JOB_NUMBER") || "Contractor's job number"}
            value={urakoitsijaNumber}
            onChange={(e) => setUrakoitsijaNumber(e.target.value)}
            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACTOR_WORK_NUMBER") || "Enter contractor work number"}
          />
        </Group>

        <Group grow>
          <DatePickerInput
            label={getTranslation("LABEL_MEASUREMENT_PVM") || "Measurement PVM"}
            value={mittausPvm}
            onChange={setMittausPvm}
            placeholder={getTranslation("PLACEHOLDER_SELECT_MEASUREMENT_DATE") || "Select measurement date"}
            valueFormat="DD.MM.YYYY"
          />
        </Group>

        <Textarea
          label={getTranslation("LABEL_PERFORMERS_OF_THE_MEASUREMENT") || "Performers of the measurement"}
          value={mittausPerformers}
          onChange={(e) => setMittausPerformers(e.target.value)}
          placeholder={getTranslation("PLACEHOLDER_ENTER_MEASUREMENT_PERFORMERS") || "Enter measurement performers"}
          minRows={3}
        />

        <Group grow>
          <DatePickerInput
            label={getTranslation("LABEL_POSITION_MEASUREMENT_PVM") || "Position measurement PVM"}
            value={positionMeasurementPvm}
            onChange={setPositionMeasurementPvm}
            placeholder={getTranslation("PLACEHOLDER_SELECT_POSITION_MEASUREMENT_DATE") || "Select position measurement date"}
            valueFormat="DD.MM.YYYY"
          />
        </Group>

        <Textarea
          label={getTranslation("LABEL_PERFORMERS_OF_LOCATION_MEASUREMENT") || "Performers of location measurement"}
          value={positionPerformers}
          onChange={(e) => setPositionPerformers(e.target.value)}
          placeholder={getTranslation("PLACEHOLDER_ENTER_LOCATION_MEASUREMENT_PERFORMERS") || "Enter location measurement performers"}
          minRows={3}
        />

        {/* Diary Entries Table */}
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Text fw={500}>
              {getTranslation("LABEL_DIARY_ENTRIES") || "Diary Entries"}
            </Text>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => openPrefilledDiaryModal(false)}
              size="sm"
              //disabled={!items.length}
            >
              {getTranslation("LABEL_ADD_NEW") || "Add New"}
            </Button>
            {/* <Menu>
              <Menu.Target>
                <ActionIcon variant="outline">
                  <IconDotsVertical size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => setDiaryModalOpened(true)}>
                  Add New
                </Menu.Item>
              </Menu.Dropdown>
            </Menu> */}
          </Group>

          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{
                    textAlign: "center",
                  }}
                >
                  {getTranslation("LABEL_DATE") || "Date"}
                </Table.Th>
                <Table.Th
                  style={{
                    textAlign: "center",
                  }}
                >
                  {getTranslation("LABEL_AMENDED") || "Amended"}
                </Table.Th>
                <Table.Th
                  style={{
                    textAlign: "center",
                  }}
                >
                  {getTranslation("LABEL_ACTIONS") || "Actions"}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {diaryEntries.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text c="dimmed" ta="center">
                      {getTranslation("LABEL_NO_DIARY_ENTRIES_YET") ||
                        "No diary entries yet"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                diaryEntries.map((entry) => (
                  <Table.Tr key={entry.id}>
                    <Table.Td
                      style={{
                        borderLeft: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {entry.date?.toLocaleDateString() ||
                        getTranslation("LABEL_NO_DATE") ||
                        "No date"}
                    </Table.Td>
                    <Table.Td
                      style={{
                        borderLeft: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {entry.amended}
                    </Table.Td>
                    <Table.Td
                      style={{
                        borderLeft: "1px solid #ddd",
                        justifyItems: "center",
                      }}
                    >
                      <Group gap="xs">
                        <ActionIcon
                          variant="subtle"
                          aria-label={getTranslation("ARIA_EDIT") || "Edit"}
                          onClick={() => handleEditDiaryEntry(entry)}
                          disabled={!canModify}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={getTranslation("ARIA_DELETE") || "Delete"}
                          onClick={() => handleDeleteDiaryEntry(entry.id)}
                          disabled={!canModify}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                    {/* <Table.Td>
                      <Menu>
                        <Menu.Target>
                          <ActionIcon variant="outline" size="sm">
                            <IconDotsVertical size={14} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item>Edit</Menu.Item>
                          <Menu.Item color="red">Delete</Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td> */}
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      </Stack>
      <Group justify="flex-end" mt="md">
        {/* <Button variant="light" onClick={cancelDiaryChanges} mr="sm">
          Cancel
        </Button> */}
        <Button onClick={saveDiaryData} disabled={!canModify}>
          {getTranslation("LABEL_SAVE") || "Save"}
        </Button>
      </Group>

      {/* Diary Entry Modal */}
      <AddEditDiaryEntryModal
        key={editingDiaryId ?? `new-${modalResetToken}`}
        opened={diaryModalOpened}
        onClose={() => setDiaryModalOpened(false)}
        formValues={modalForm}
        setFormValues={setModalForm}
        onSave={handleAddDiaryEntry}
        templateOptions={templateOptions}
        supervisorOptions={supervisorOptions}
        foremanOptions={foremanOptions}
        foreman2Options={foreman2Options}
        title={getTranslation("TITLE_ADD_A_JOURNAL_ENTRY") || "Add a Journal Entry"}
        objectId={objectId}
        contractId={contractId}
        diaryId={editingDiaryId ?? undefined}
        currentUserId={userId}
        addressSystemId={addrId}
      />
    </>
  );
}
